import { companyData as defaultCompanyData } from '@/mocks/company';
import {
  type TemplateConfig,
  getOverlayClass,
} from '@/mocks/template-config';
import type { CompanyData } from '@/hooks/useCompanyData';

interface FooterSectionProps {
  config: TemplateConfig['footer'];
  specialties: TemplateConfig['specialties'];
  companyData?: CompanyData;
}

export default function FooterSection({ config, specialties, companyData }: FooterSectionProps) {
  if (!config.show) return null;

  const cData = companyData || defaultCompanyData;

  const activeSpecialties = specialties
    .filter((s) => s.active)
    .sort((a, b) => a.order - b.order);

  // Banner Full Mode
  if (config.mode === 'banner_full' && config.bannerImage) {
    return (
      <footer className="bg-brand-darker border-t border-brand-gold/10">
        <div className="relative">
          <img
            src={config.bannerImage}
            alt="Rodapé"
            className="w-full h-auto object-cover"
          />
          {config.overlay !== 'none' && (
            <div className={`absolute inset-0 ${getOverlayClass(config.overlay)}`} />
          )}
        </div>
        {/* Hidden SEO data for accessibility and PDF */}
        <div className="sr-only">
          <span>{cData.name}</span>
          <span>{cData.phone1}</span>
          <span>{cData.phone2}</span>
          <span>{cData.email}</span>
          <span>{cData.website}</span>
          <span>{cData.city}</span>
          <span>{cData.cnpj}</span>
        </div>
        {/* Bottom tagline */}
        <div className="border-t border-brand-gold/10">
          <div className="max-w-5xl mx-auto px-4 py-4 text-center">
            <p className="text-brand-gold text-sm">
              {cData.name} – Tradição, tecnologia e confiança desde {cData.foundedYear}.
            </p>
          </div>
        </div>
      </footer>
    );
  }

  // Dynamic Mode
  return (
    <footer className="bg-brand-darker border-t border-brand-gold/10">
      {/* Main Footer */}
      <div className="max-w-5xl mx-auto px-4 py-8 md:py-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 md:gap-4">
          {/* QR Code + Text */}
          {config.qrCode.enabled && (
            <div className="flex items-center gap-4">
              <img
                src={config.qrCode.image}
                alt="QR Code"
                className="w-20 h-20 rounded-md object-cover"
              />
              <p className="text-brand-text-muted text-xs leading-relaxed">
                Acesse sua proposta escaneando o QR Code ou faça login no portal.
              </p>
            </div>
          )}

          {/* Contacts */}
          <div className="space-y-2">
            {config.showWhatsApp && (
              <a
                href={`https://wa.me/${cData.phone1.replace(/\D/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 hover:text-brand-gold transition-colors"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-whatsapp-line text-brand-gold text-sm" />
                </div>
                <span className="text-brand-text text-sm">{cData.phone1}</span>
              </a>
            )}
            {config.showPhones && cData.phone2 && (
              <a
                href={`https://wa.me/${cData.phone2.replace(/\D/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 hover:text-brand-gold transition-colors"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-whatsapp-line text-brand-gold text-sm" />
                </div>
                <span className="text-brand-text text-sm">{cData.phone2}</span>
              </a>
            )}
            {config.showEmail && (
              <a
                href={`mailto:${cData.email}`}
                className="flex items-center gap-2 hover:text-brand-gold transition-colors"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-mail-line text-brand-gold text-sm" />
                </div>
                <span className="text-brand-text text-sm">{cData.email}</span>
              </a>
            )}
            {config.showWebsite && (
              <a
                href={`https://${cData.website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 hover:text-brand-gold transition-colors"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-global-line text-brand-gold text-sm" />
                </div>
                <span className="text-brand-text text-sm">{cData.website}</span>
              </a>
            )}
          </div>

          {/* Address + Hours */}
          <div className="space-y-2">
            {config.showCity && (
              <div className="flex items-start gap-2">
                <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                  <i className="ri-map-pin-line text-brand-gold text-sm" />
                </div>
                <span className="text-brand-text text-sm">
                  {cData.city} – {cData.state}
                </span>
              </div>
            )}
            {config.showHours && (
              <>
                <div className="flex items-start gap-2">
                  <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                    <i className="ri-time-line text-brand-gold text-sm" />
                  </div>
                  <span className="text-brand-text text-sm">{cData.workHours.weekday}</span>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                    <i className="ri-time-line text-brand-gold text-sm" />
                  </div>
                  <span className="text-brand-text text-sm">{cData.workHours.saturday}</span>
                </div>
              </>
            )}
          </div>

          {/* CNPJ */}
          {config.showCNPJ && (
            <div className="flex flex-col justify-center">
              <span className="text-brand-text-muted text-xs">CNPJ:</span>
              <span className="text-brand-text text-sm">{cData.cnpj}</span>
            </div>
          )}
        </div>
      </div>

      {/* Specialties Bar */}
      {config.showSpecialties && activeSpecialties.length > 0 && (
        <div className="border-t border-brand-gold/10">
          <div className="max-w-5xl mx-auto px-4 py-4">
            <div className="flex flex-wrap items-center justify-center gap-4 md:gap-8">
              {activeSpecialties.map((spec, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className={`${spec.icon} text-brand-gold text-sm`} />
                  </div>
                  <span className="text-brand-text-muted text-xs uppercase tracking-wide">
                    {spec.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Bottom Bar */}
      <div className="border-t border-brand-gold/10">
        <div className="max-w-5xl mx-auto px-4 py-4 text-center">
          <p className="text-brand-gold text-sm">
            {cData.name} – Tradição, tecnologia e confiança desde {cData.foundedYear}.
          </p>
        </div>
      </div>
    </footer>
  );
}